/**
 * IssuesPanel — the project workspace "Issues" tab.
 *
 * Renders an inbox of open clause flags aggregated across the
 * project's document versions. Replaces the redundant "Chat" tab that
 * used to live in the project banner. Backed by
 * `GET /api/projects/:projectId/clause-flags?status=pending`.
 *
 * The panel is presentational — it does not fetch. The parent owns
 * the fetch (because the same data drives the Issues tab badge in the
 * banner, which has to render even when the panel itself isn't
 * mounted), passes the response down, and exposes a refresh callback.
 *
 * Status mutations (accept/dismiss/escalate) happen in the document
 * workspace, not here. The parent listens for the
 * `corner:clause-flags-updated` window event (see
 * `notifyClauseFlagsUpdated` below) and refetches.
 *
 * See `docs/plans/2026-05-08-project-issues-tab-design.md` for the
 * full design rationale, including why we ship Scope 1 (clause flags
 * only) before scopes 2/3/4 (signatures / comments / approvals).
 */

import { useCallback, useState } from 'react';
import type {
  ClauseFlagSeverity,
  ProjectClauseFlagsResponse,
  ProjectClauseFlagSummary,
} from '@corner/shared';
import { APP } from './tokens';

const SEVERITY_LABEL: Record<ClauseFlagSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

/**
 * Severity dot colors. Critical/high lean on the same warm warning
 * tone the rest of the app uses for risk indicators
 * (`textReviewPriorityHigh`); medium/low/info step down to neutral
 * grays so the panel doesn't look like a Christmas tree.
 */
const SEVERITY_COLOR: Record<ClauseFlagSeverity, string> = {
  critical: '#C04A2A',
  high: '#D97645',
  medium: '#9A8B7A',
  low: '#A89E91',
  info: '#B7AFA4',
};

/**
 * Window event the parent dispatches after any flag-status mutation
 * to invite a refetch. Centralized so panel + emitters share a single
 * string constant.
 */
export const CLAUSE_FLAGS_UPDATED_EVENT = 'corner:clause-flags-updated';

export type IssuesPanelOnOpenFlag = (target: {
  reviewId: string;
  flagId: string;
  versionId: string;
  fileId: string;
}) => void;

export default function IssuesPanel({
  data,
  loading,
  error,
  onRetry,
  activeVersionId,
  onOpenFlag,
  onRunClauseReview,
  onLaunchSetup,
}: {
  data: ProjectClauseFlagsResponse | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /**
   * Currently-active version in the Documents tab when the user
   * switched to Issues. Used as the default target for the empty-state
   * "Run clause review" CTA so the click does something obvious
   * instead of guessing a version.
   */
  activeVersionId: string | null;
  /**
   * Click handler for "Open in doc". The parent is responsible for
   * switching the workspace tab back to Documents, setting the active
   * version, and (optionally) scrolling/highlighting the flag via the
   * existing flag-locate flow.
   */
  onOpenFlag: IssuesPanelOnOpenFlag;
  /**
   * Click handler for the empty-state "Run clause review" CTA. Hidden
   * when no version is currently active in the Documents tab (we
   * surface a hint instead). Optional so this panel is still usable
   * in a context where launching a review doesn't make sense.
   */
  onRunClauseReview?: (versionId: string) => void;
  /**
   * Open the clause-review setup strip from the panel header. Distinct
   * from `onRunClauseReview` (which targets the empty-state CTA against
   * the currently-active version): this is a generic "I want to start
   * a new review" affordance that's visible whether or not the
   * project already has flags. The parent owns persona/jurisdiction/
   * source resolution; this prop just opens the setup UI.
   */
  onLaunchSetup?: () => void;
}) {
  const handleRunClauseReview = useCallback(() => {
    if (!activeVersionId || !onRunClauseReview) return;
    onRunClauseReview(activeVersionId);
  }, [activeVersionId, onRunClauseReview]);

  const groups = data?.groups ?? [];
  const isEmpty = !loading && !error && groups.length === 0;

  return (
    <div
      style={{
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        backgroundColor: APP.bgContent,
        overflow: 'auto',
      }}
    >
      <div style={{ padding: '20px 24px 32px', maxWidth: 880, width: '100%', margin: '0 auto' }}>
        <PanelHeader
          totalCount={data?.totalCount ?? 0}
          loading={loading}
          onLaunchSetup={onLaunchSetup}
        />

        {error && (
          <ErrorState message={error} onRetry={onRetry} />
        )}

        {isEmpty && !error && (
          <EmptyState
            canRun={Boolean(activeVersionId && onRunClauseReview)}
            onRun={handleRunClauseReview}
          />
        )}

        {groups.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 16 }}>
            {groups.map((group) => (
              <FlagGroup
                key={group.versionId}
                versionId={group.versionId}
                fileId={group.fileId}
                versionLabel={group.versionLabel}
                flags={group.flags}
                onOpenFlag={onOpenFlag}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PanelHeader({
  totalCount,
  loading,
  onLaunchSetup,
}: {
  totalCount: number;
  loading: boolean;
  onLaunchSetup?: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <h2
          style={{
            margin: 0,
            fontFamily: APP.fontFamily,
            fontSize: 18,
            fontWeight: 600,
            color: APP.textPrimary,
          }}
        >
          Clause flags
        </h2>
        <p
          style={{
            margin: '4px 0 0',
            fontFamily: APP.fontFamily,
            fontSize: 12,
            color: APP.textMuted,
          }}
        >
          Open clause flags across this project. Click a flag to jump to its location in the document.
        </p>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: APP.fontFamily,
            fontSize: 11,
            color: APP.textMuted,
            minWidth: 60,
            textAlign: 'right',
          }}
        >
          {loading ? 'Loading…' : `${totalCount} open`}
        </span>
        {onLaunchSetup && (
          <button
            type="button"
            onClick={onLaunchSetup}
            style={{
              fontFamily: APP.fontFamily,
              fontSize: 12,
              fontWeight: 500,
              padding: '6px 12px',
              border: `1px solid ${APP.border}`,
              borderRadius: 5,
              backgroundColor: APP.bgContent,
              color: APP.textPrimary,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Run new review
          </button>
        )}
      </div>
    </div>
  );
}

function FlagGroup({
  versionId,
  fileId,
  versionLabel,
  flags,
  onOpenFlag,
}: {
  versionId: string;
  fileId: string;
  versionLabel: string;
  flags: ProjectClauseFlagSummary[];
  onOpenFlag: IssuesPanelOnOpenFlag;
}) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          paddingBottom: 6,
          borderBottom: `1px solid ${APP.border}`,
        }}
      >
        <span
          style={{
            fontFamily: APP.fontFamily,
            fontSize: 13,
            fontWeight: 600,
            color: APP.textPrimary,
          }}
        >
          {versionLabel}
        </span>
        <span
          style={{
            fontFamily: APP.fontFamily,
            fontSize: 11,
            color: APP.textMuted,
          }}
        >
          {flags.length} open
        </span>
      </header>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {flags.map((flag) => (
          <FlagRow
            key={`${versionId}:${flag.flagId}`}
            flag={flag}
            onOpen={() =>
              onOpenFlag({
                reviewId: flag.reviewId,
                flagId: flag.flagId,
                versionId,
                fileId,
              })
            }
          />
        ))}
      </div>
    </section>
  );
}

function FlagRow({
  flag,
  onOpen,
}: {
  flag: ProjectClauseFlagSummary;
  onOpen: () => void;
}) {
  const [hovering, setHovering] = useState(false);
  const dotColor = SEVERITY_COLOR[flag.severity];
  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        display: 'flex',
        gap: 12,
        padding: '12px 4px',
        borderBottom: `1px solid ${APP.border}`,
        alignItems: 'flex-start',
        backgroundColor: hovering ? 'var(--app-bg-history-active)' : 'transparent',
        transition: 'background-color 120ms ease',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          backgroundColor: dotColor,
          flexShrink: 0,
          marginTop: 7,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'baseline',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: APP.fontFamily,
              fontSize: 11,
              fontWeight: 600,
              color: dotColor,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {SEVERITY_LABEL[flag.severity]}
          </span>
          <span
            style={{
              fontFamily: APP.fontFamily,
              fontSize: 13,
              fontWeight: 500,
              color: APP.textPrimary,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {flag.issue || 'Untitled flag'}
          </span>
        </div>
        {flag.evidenceQuote && (
          <p
            style={{
              margin: '4px 0 0',
              fontFamily: APP.fontFamily,
              fontSize: 12,
              color: APP.textSecondary,
              lineHeight: 1.5,
              fontStyle: 'italic',
              wordBreak: 'break-word',
            }}
          >
            “{flag.evidenceQuote}”
          </p>
        )}
        {flag.sectionRef && (
          <p
            style={{
              margin: '4px 0 0',
              fontFamily: APP.fontFamily,
              fontSize: 11,
              color: APP.textMuted,
            }}
          >
            {flag.sectionRef}
            {flag.pageHint != null ? ` · p.${flag.pageHint}` : ''}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onOpen}
        style={{
          fontFamily: APP.fontFamily,
          fontSize: 12,
          fontWeight: 500,
          padding: '6px 10px',
          border: `1px solid ${APP.border}`,
          borderRadius: 5,
          backgroundColor: APP.bgContent,
          color: APP.textPrimary,
          cursor: 'pointer',
          flexShrink: 0,
          opacity: hovering ? 1 : 0.85,
          transition: 'opacity 120ms ease',
        }}
      >
        Open in doc
      </button>
    </div>
  );
}

function EmptyState({ canRun, onRun }: { canRun: boolean; onRun: () => void }) {
  return (
    <div
      style={{
        marginTop: 32,
        padding: '32px 24px',
        border: `1px dashed ${APP.border}`,
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <p
        style={{
          margin: 0,
          fontFamily: APP.fontFamily,
          fontSize: 14,
          fontWeight: 600,
          color: APP.textPrimary,
        }}
      >
        No open clause flags.
      </p>
      <p
        style={{
          margin: 0,
          fontFamily: APP.fontFamily,
          fontSize: 12,
          color: APP.textMuted,
          maxWidth: 420,
        }}
      >
        Run a clause review on this project's active document to surface risks, missing clauses, and one-sided language.
      </p>
      <button
        type="button"
        onClick={onRun}
        disabled={!canRun}
        title={canRun ? undefined : 'Open a PDF or Word version in Documents first'}
        style={{
          marginTop: 4,
          fontFamily: APP.fontFamily,
          fontSize: 12,
          fontWeight: 600,
          padding: '8px 14px',
          border: canRun ? 'none' : `1px solid ${APP.border}`,
          borderRadius: 8,
          backgroundColor: canRun ? '#D97645' : APP.bgContent,
          color: canRun ? '#FDFCFB' : APP.textMuted,
          cursor: canRun ? 'pointer' : 'not-allowed',
          opacity: canRun ? 1 : 0.85,
          boxShadow: canRun ? '0 1px 2px rgba(26,23,20,0.08)' : 'none',
        }}
      >
        Run clause review
      </button>
      {!canRun && (
        <p
          style={{
            margin: 0,
            fontFamily: APP.fontFamily,
            fontSize: 11,
            color: APP.textMuted,
          }}
        >
          Open a PDF or Word version in Documents first.
        </p>
      )}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      style={{
        marginTop: 24,
        padding: '16px 20px',
        border: `1px solid ${APP.border}`,
        borderRadius: 6,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: APP.bgContent,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: APP.fontFamily,
            fontSize: 13,
            fontWeight: 500,
            color: APP.textPrimary,
          }}
        >
          Couldn't load clause flags.
        </p>
        <p
          style={{
            margin: '2px 0 0',
            fontFamily: APP.fontFamily,
            fontSize: 11,
            color: APP.textMuted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {message}
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        style={{
          fontFamily: APP.fontFamily,
          fontSize: 12,
          fontWeight: 500,
          padding: '6px 12px',
          border: `1px solid ${APP.border}`,
          borderRadius: 5,
          backgroundColor: APP.bgContent,
          color: APP.textPrimary,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        Retry
      </button>
    </div>
  );
}

/**
 * Helper for callers that mutate flag status — dispatches the event
 * IssuesPanel listens for. Importing this helper instead of typing
 * the event name in scattered call sites keeps the contract in one
 * place.
 */
export function notifyClauseFlagsUpdated(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CLAUSE_FLAGS_UPDATED_EVENT));
}
